// Import main CSS
import './index.css';
console.log('Slim Volume Parcel environment is working!');

// Import JS modules
import './js/nav.js';
// Register the <lite-youtube> custom element
import "lite-youtube-embed";

// Optional: their default styles (you can skip if you want to style it yourself)
import "lite-youtube-embed/src/lite-yt-embed.css";

import './js/scroll-to-top.js';

import {
  ensureGlobalPlayer,
  initPageFromConfig,
  initAlbumTrackClickPlayback,
  reinitLyricsSyncForCurrentPage,
} from './audio-player/audio-player.js';

import './audio-player/lyrics-sync.js';
import { initMusicPjax } from './audio-player/music-pjax.js';

// Ensure only one audio source plays at a time.
// If any non-player <audio> or <video> element starts playing,
// pause the global SVPlayer.
(function initGlobalMediaInterlock() {
  document.addEventListener(
    "play",
    (event) => {
      const target = event.target;
      if (!(target instanceof HTMLMediaElement)) return;

      // Ignore the main player’s own audio element
      const mainAudio =
        document.getElementById("audioElement") ||
        document.querySelector("[data-sv-audio-element]");
      if (mainAudio && target === mainAudio) {
        return;
      }

      // At this point, some *other* audio/video started playing.
      const SVPlayer = window.SVPlayer;
      if (SVPlayer && typeof SVPlayer.pause === "function") {
        try {
          SVPlayer.pause();
        } catch (err) {
          console.warn("[SVPlayer] Failed to pause global player:", err);
        }
      }
    },
    true // use capture phase because 'play' does not bubble
  );
})();

// Pause SVPlayer when a lite-youtube embed is activated
(function initLiteYoutubeInterlock() {
  document.addEventListener(
    "click",
    (event) => {
      const lite = event.target.closest("lite-youtube");
      if (!lite) return;

      const SVPlayer = window.SVPlayer;
      if (SVPlayer && typeof SVPlayer.pause === "function") {
        try {
          SVPlayer.pause();
        } catch (err) {
          console.warn(
            "[SVPlayer] Failed to pause global player from lite-youtube click:",
            err
          );
        }
      }
    },
    true // capture so we see it even if inner handlers stop propagation
  );
})();

// Song-page hero overlay: "play this song / switch to this song"
function initSongHeroPlayButton() {
  const hero = document.querySelector('[data-sv-song-hero]');

  if (!hero) {
    if (typeof window !== 'undefined') {
      window.svSyncSongHeroButton = null;
    }
    return;
  }

  const button = hero.querySelector('[data-sv-song-play]');
  if (!button) {
    if (typeof window !== 'undefined') {
      window.svSyncSongHeroButton = null;
    }
    return;
  }

  const nowPlayingBadge = hero.querySelector('[data-sv-song-nowplaying]') || null;

  const heroAudioUrlRaw = hero.getAttribute('data-sv-track-audio') || '';
  const heroAudioUrl = heroAudioUrlRaw.trim();

  const heroTrackSlugRaw = hero.getAttribute('data-sv-track-slug') || '';
  const heroTrackSlug = heroTrackSlugRaw.trim().toLowerCase();

  let pageTrack = null;

  const configScript = document.querySelector(
    'script[type="application/json"][data-sv-player-config]'
  );
  if (configScript && configScript.textContent.trim()) {
    try {
      const cfg = JSON.parse(configScript.textContent);
      if (cfg && typeof cfg === 'object' && cfg.track) {
        pageTrack = cfg.track;
      }
    } catch (err) {
      console.warn('[SongHero] Failed to parse player config JSON', err);
    }
  }

  function fallbackTrack() {
    const fromConfig = pageTrack || {};

    const slug =
      heroTrackSlug ||
      (fromConfig.slug || fromConfig.songSlug || fromConfig.id || '');

    const audioUrl =
      heroAudioUrl ||
      fromConfig.audioUrl ||
      (fromConfig.links && fromConfig.links.mp3) ||
      '';

    const idFromDataset = hero.dataset.svTrackId || '';

    const id =
      slug ||
      idFromDataset ||
      heroAudioUrl ||
      fromConfig.id ||
      '';

    return {
      ...fromConfig,
      id,
      slug: slug || null,
      songSlug: slug || null,
      audioUrl,
    };
  }

  function getPageTrack() {
    return pageTrack || fallbackTrack();
  }

  function updateHeroButtonState(trackFromPlayer) {
    const SVPlayer = window.SVPlayer;
    const audioEl = SVPlayer && SVPlayer.audioElement;

    const isPlaying =
      SVPlayer && typeof SVPlayer.isPlaying === 'function'
        ? SVPlayer.isPlaying()
        : !!(audioEl && !audioEl.paused && !audioEl.ended);

    const currentTrack =
      trackFromPlayer ||
      (SVPlayer && typeof SVPlayer.getCurrentTrack === 'function'
        ? SVPlayer.getCurrentTrack()
        : null);

    const thisTrack = getPageTrack();

    let isSameTrackBySlug = false;

    if (currentTrack && thisTrack) {
      const currentSlug =
        (currentTrack.slug ||
          currentTrack.songSlug ||
          currentTrack.id ||
          '')
          .toString()
          .toLowerCase();

      const thisSlug =
        (thisTrack.slug ||
          thisTrack.songSlug ||
          thisTrack.id ||
          heroTrackSlug ||
          '')
          .toString()
          .toLowerCase();

      isSameTrackBySlug =
        !!currentSlug &&
        !!thisSlug &&
        currentSlug === thisSlug;
    }

    const shouldShowPause = isSameTrackBySlug && isPlaying;

    button.dataset.state = shouldShowPause ? 'pause' : 'play';
    hero.classList.toggle('is-playing', !!shouldShowPause);
    hero.classList.toggle('is-not-playing', !shouldShowPause);

    if (nowPlayingBadge) {
      const showBadge = isSameTrackBySlug && isPlaying;
      nowPlayingBadge.hidden = !showBadge;
    }

    const labelBase =
      thisTrack.songTitle ||
      thisTrack.title ||
      thisTrack.id ||
      '';
    const labelVerb = shouldShowPause ? 'Pause' : 'Play';
    const ariaLabel = labelBase ? `${labelVerb} ${labelBase}` : labelVerb;
    button.setAttribute('aria-label', ariaLabel);
  }

  if (typeof window !== 'undefined') {
    window.svSyncSongHeroButton = updateHeroButtonState;
  }

  // Initial sync on page load
  updateHeroButtonState(null);

  button.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();

    const SVPlayer = window.SVPlayer;
    if (!SVPlayer || !SVPlayer.audioElement) return;

    const thisTrack = getPageTrack();

    const audioEl = SVPlayer.audioElement;
    const isPlaying =
      SVPlayer && typeof SVPlayer.isPlaying === 'function'
        ? SVPlayer.isPlaying()
        : !!(audioEl && !audioEl.paused && !audioEl.ended);

    const currentTrack =
      typeof SVPlayer.getCurrentTrack === 'function'
        ? SVPlayer.getCurrentTrack()
        : null;

    let isSameTrackByUrl = false;

    if (currentTrack && thisTrack) {
      const currentUrl =
        (currentTrack.audioUrl ||
          (currentTrack.links && currentTrack.links.mp3) ||
          '')
          .split(/[?#]/)[0];

      const thisUrl =
        (thisTrack.audioUrl ||
          (thisTrack.links && thisTrack.links.mp3) ||
          heroAudioUrl ||
          '')
          .split(/[?#]/)[0];

      isSameTrackByUrl =
        !!currentUrl &&
        !!thisUrl &&
        currentUrl === thisUrl;
    }

    if (isSameTrackByUrl) {
      if (isPlaying) {
        SVPlayer.pause();
        updateHeroButtonState(null);
      } else {
        SVPlayer.play();
      }
    } else {
      SVPlayer.loadTrack(thisTrack, { autoplay: true, reset: true });
    }
  });
}

// Single page init hook, used on initial load and after each PJAX swap
function svPageInit() {
  const app = ensureGlobalPlayer();
  if (!app) {
    return;
  }

  // 1) Read per-page JSON config ({ playlist } or { track })
  initPageFromConfig();

  // 2) Wire album play icons to use the playlist when present
  initAlbumTrackClickPlayback();

  // 3) Wire song header art overlay (if present on this page)
  initSongHeroPlayButton();

  // 4) (Re)wire synced lyrics for this page's DOM
  reinitLyricsSyncForCurrentPage(app.audioElement);
}

// Expose for music-pjax.js
window.svPageInit = svPageInit;

document.addEventListener('DOMContentLoaded', () => {
  // Initial page load
  svPageInit();
  // Set up PJAX navigation for /music/* links
  initMusicPjax();
});

console.log('Parcel is definitely rebuilding!');